import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { candidateApi } from "@/lib/candidateApi";
import { applyBranding } from "@/lib/branding";
import { useInvite } from "@/hooks/useInvite";
import { CandidateFlow } from "@/shell/CandidateFlow";
import { Button, Card, Label, Field, Textarea, Spinner, Alert } from "@/components/primitives";
import { InviteError } from "./InviteError";
import type { SetupData, ProfileData } from "@/lib/types";

// Analysis overlay — shown while prep processes after submit.
const ANALYSIS_STEPS = [
  "Reading your CV…",
  "Mapping your experience to this role…",
  "Identifying your key strengths…",
  "Calibrating question difficulty…",
  "Preparing your tailored question set…",
];

function AnalysisOverlay({ cvName }: { cvName?: string }) {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setStep((s) => Math.min(s + 1, ANALYSIS_STEPS.length - 1)), 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-paper/80 backdrop-blur-sm">
      <div className="mx-4 max-w-sm rounded-xl border border-hair bg-cream p-7 text-center shadow-md">
        <div className="mb-4 flex justify-center">
          <Spinner size={28} />
        </div>
        <p className="text-sm font-medium text-ink">{ANALYSIS_STEPS[step]}</p>
        {cvName && step >= 1 && (
          <p className="mt-1.5 font-mono text-[11px] text-slate-400">{cvName}</p>
        )}
        <p className="mt-3 text-xs text-slate-400">
          This takes 10–20 seconds — your session will begin immediately after.
        </p>
      </div>
    </div>
  );
}

// Session setup — Layer 2 of the KB split. Collects per-session role context
// (JD URL, notes, VAD speed) and an optional CV override for this session only.
// The master CV (Layer 1) lives on the candidate profile (/profile).
export function Setup() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { data: invite, error: inviteErr, loading: inviteLoading } = useInvite(token);

  const [setup, setSetup] = useState<SetupData | null>(null);
  const [gateError, setGateError] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);

  const [overrideFile, setOverrideFile] = useState<File | null>(null);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [jdUrl, setJdUrl] = useState("");
  const [vadSpeed, setVadSpeed] = useState("fast");
  const [notes, setNotes] = useState("");
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [analysing, setAnalysing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!token) return;
    let live = true;
    candidateApi.getSetup(token).then((res) => {
      if (!live) return;
      if (res.ok) setSetup(res);
      else {
        if (res.next === "verify") navigate(`/invite/${token}/verify`);
        else setGateError(res.error);
      }
    });
    // Load profile to check master CV (Layer 1). Degrades gracefully if API not available.
    candidateApi.getProfile(token).then((res) => {
      if (!live) return;
      setProfileLoaded(true);
      if (res.ok) setProfile(res as ProfileData);
    });
    return () => { live = false; };
  }, [token, navigate]);

  useEffect(() => {
    if (invite?.branding) applyBranding(invite.branding);
  }, [invite]);

  // Profile gate: only block when profile API responded and explicitly says no CV.
  // Falls through (allows start) if profile API is unavailable (404/error).
  const noMasterCv = profileLoaded && profile !== null && !profile.hasCv;

  async function submit() {
    if (!token || !setup) return;
    if (noMasterCv) return;
    setBusy(true);
    setAnalysing(true);
    setErr(null);
    const fd = new FormData();
    if (overrideFile) fd.append("cv_file", overrideFile);
    if (jdUrl.trim()) fd.append("jd_url", jdUrl.trim());
    if (notes.trim()) fd.append("custom_instructions", notes.trim());
    fd.append("vad_speed", vadSpeed);
    const r = await candidateApi.start(token, fd);
    if (r.ok && r.sessionId) {
      // Route through notification pre-prompt (C7) before prep loading.
      navigate(`/invite/${token}/notify/${r.sessionId}`);
      return;
    }
    setBusy(false);
    setAnalysing(false);
    setErr(r.error ?? "Something went wrong. Please try again.");
  }

  if (inviteErr || gateError) return <InviteError message={inviteErr ?? gateError ?? "Invite not found."} />;
  if (inviteLoading || !setup) {
    return (
      <CandidateFlow>
        <div className="flex justify-center py-10"><Spinner size={28} /></div>
      </CandidateFlow>
    );
  }

  const agencyProps = {
    agencyName: invite?.agencyName,
    logoUrl: invite?.branding?.logoUrlLight || undefined,
    logoFilter: invite?.branding?.logoFilter,
  };

  return (
    <CandidateFlow {...agencyProps} width="wide">
      {analysing && <AnalysisOverlay cvName={overrideFile?.name} />}
      <Link to={`/invite/${token}`} className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-ink">
        <span aria-hidden="true">←</span> Back
      </Link>
      <h1 className="mb-5 text-xl font-semibold text-ink">Prepare your session</h1>

      <div className="mb-5 rounded-md border border-hair bg-paper-2 px-4 py-3.5 text-xs leading-relaxed text-slate-500">
        Your CV and job description personalise every question your coach asks. Upload both so
        the practice matches the role and company you are going for.
      </div>

      <Card className="mb-5 space-y-2">
        <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">
          Session details
        </div>
        {[
          ["Role", setup.role],
          ["Company", setup.company],
          ["Interviewer", setup.personaLabel],
          ["Mode", setup.modeLabel],
        ].map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-sm">
            <span className="font-mono text-xs text-slate-500">{k}</span>
            <span className="font-medium text-ink">{v}</span>
          </div>
        ))}
      </Card>

      {/* CV — two-layer split */}
      <div className="mb-5">
        <Label>CV</Label>

        {noMasterCv ? (
          /* No master CV: gate with profile prompt */
          <div className="space-y-3">
            <Alert variant="warn">
              Your profile doesn't have a CV yet. Upload your master CV to your profile first — it personalises every session.
            </Alert>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => navigate(`/invite/${token}/profile`)}
            >
              Go to Profile →
            </Button>
          </div>
        ) : overrideFile ? (
          /* Session-override file selected */
          <div className="flex items-center justify-between rounded-md border border-hair bg-paper-2 px-4 py-3">
            <div>
              <span className="inline-flex items-center gap-2 text-sm text-good">
                <svg width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <path d="M5 10.5l3.5 3.5L15 6.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Using {overrideFile.name} for this session
              </span>
              <div className="mt-0.5 text-[11px] text-slate-500">This replaces your profile CV for this session only</div>
            </div>
            <button
              type="button"
              onClick={() => { setOverrideFile(null); setOverrideOpen(false); }}
              className="ml-3 rounded px-2 py-1 font-mono text-xs text-slate-500 hover:text-ink"
            >
              Remove ×
            </button>
          </div>
        ) : (
          /* Master CV in place — show accordion for optional override */
          <div className="overflow-hidden rounded-md border border-hair">
            <button
              type="button"
              onClick={() => setOverrideOpen((o) => !o)}
              className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-paper-2"
            >
              <div>
                <div className="text-sm font-medium text-ink">
                  Using your profile CV
                  {profile?.filename && (
                    <span className="ml-1.5 font-mono text-[11px] font-normal text-slate-500">
                      ({profile.filename})
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-[11px] text-slate-500">
                  Your coach will use this to personalise every question
                </div>
              </div>
              <span className="shrink-0 text-xs font-medium text-accent">
                {overrideOpen ? "Cancel ↑" : "Use a different CV for this session ↓"}
              </span>
            </button>

            {overrideOpen && (
              <div className="border-t border-hair px-4 pb-4 pt-3">
                <p className="mb-3 text-xs text-slate-500">
                  This replaces your profile CV for this session only. Your master CV is unchanged.
                </p>
                <div
                  onClick={() => fileInput.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
                  onDragLeave={() => setDrag(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDrag(false);
                    const f = e.dataTransfer.files?.[0];
                    if (f) { setOverrideFile(f); setOverrideOpen(false); setErr(null); }
                  }}
                  className={`cursor-pointer rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors ${
                    drag ? "border-accent bg-accent-50" : "border-slate-300 bg-paper-2 hover:border-accent"
                  }`}
                >
                  <div className="text-sm font-medium text-ink">Drop CV here, or click to browse</div>
                  <div className="mt-1 text-xs text-slate-500">PDF or Word · max 10 MB</div>
                </div>
              </div>
            )}
          </div>
        )}

        <input
          ref={fileInput}
          type="file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            if (f) { setOverrideFile(f); setOverrideOpen(false); setErr(null); }
            e.target.value = "";
          }}
        />
      </div>

      {/* JD */}
      <div className="mb-5">
        <Label>Job description</Label>
        {setup.jdProvided ? (
          <div className="flex items-center justify-between rounded-md border border-hair bg-paper-2 px-4 py-3">
            <div>
              <div className="text-sm font-medium text-ink">Added by your recruiter</div>
              <div className="text-[11px] text-slate-500">Your coach will use this to tailor the interview</div>
            </div>
          </div>
        ) : (
          <>
            <Field type="url" placeholder="https://…" value={jdUrl} onChange={(e) => setJdUrl(e.target.value)} />
            <p className="mt-1.5 text-xs text-slate-500">Optional — paste a link to the job description.</p>
          </>
        )}
      </div>

      {/* Notes — framed as key research (STORM research framing, C6 spec) */}
      <div className="mb-6">
        <Label>Prep notes</Label>
        <Textarea
          rows={4}
          placeholder="Add any key research: company news, strategic priorities, who will be in the room, and 2–3 specific examples from your career that are relevant to this role."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
        <p className="mt-1.5 text-xs text-slate-500">
          Your coach uses these notes to tailor questions to your specific situation. The more context you add, the sharper the session.
        </p>
      </div>

      {/* VAD preference */}
      <div className="mb-6">
        <Label>Speech detection speed</Label>
        <p className="mb-1.5 text-xs text-slate-500">
          How quickly the coach responds when you stop speaking.
        </p>
        <select
          value={vadSpeed}
          onChange={(e) => setVadSpeed(e.target.value)}
          className="w-full rounded-md border border-hair bg-paper-2 px-3 py-2 text-sm text-ink"
        >
          <option value="fast">Normal (recommended)</option>
          <option value="slow">Slower — give me more time to think</option>
          <option value="very_slow">Slowest — I need extended pauses</option>
        </select>
      </div>

      {err && <Alert className="mb-4">{err}</Alert>}

      <Button variant="primary" className="w-full" onClick={submit} disabled={busy || noMasterCv}>
        {busy ? (
          <><Spinner size={16} /> Preparing…</>
        ) : (
          <>
            Continue
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M4 10h12M11 5l5 5-5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </>
        )}
      </Button>
    </CandidateFlow>
  );
}
